import React from 'react';
import ProposedAction from './ProposedAction';
import { noteCoachOutputCommitted } from '../../services/coachSequenceTiming';

export async function reconcileExerciseCoachSuggestion({ refreshWorkout, invalidateSets, resolution, workoutExerciseId }) {
  const refresh = refreshWorkout ? refreshWorkout(resolution) : Promise.resolve(true);
  // Suggestions belong to a conversation anchor, but their canonical actions
  // can change other members (including other Superset exercises). Refresh each
  // target's displayed query as well as the source; activity hydration alone
  // does not update the exercise components' independently held local sets.
  const targetIds = [...new Set([workoutExerciseId, ...(resolution?.actions || [])
    .map(action => action?.arguments?.workout_exercise_id)
    .filter(Boolean)])];
  // Start displayed-set reads immediately: canonical activity/effect
  // reconciliation must not hold them behind an unrelated Coach read.
  const [refreshed] = await Promise.all([refresh, ...targetIds.map(id => invalidateSets?.(id))]);
  return refreshed !== false;
}

export function shouldShowExerciseCoachSuggestions({ workoutStatus, readOnly = false, editable = false, pendingActionCount = 0, summary = '' } = {}) {
  return editable && !readOnly && workoutStatus === 'ongoing' && (pendingActionCount > 0 || summary.trim().length > 0);
}

function ExerciseCoachSuggestions({
  recommendation,
  workoutId,
  workoutExerciseId,
  workoutStatus,
  readOnly = false,
  editable = false,
  exerciseName,
  backendService,
  onResolved,
  onDiscuss,
  coachImageUrl = null,
  coachEmoji = '🥋',
}) {
  const pendingActions = (recommendation?.proposed_actions || []).filter(action => (
    action?.status === 'pending'
    || action?.status === 'missing'
    || (action?.status === 'applying' && ['failed', 'executed'].includes(action?.execution?.phase))
  ));
  // A hold/recovery prescription can be useful without changing any saved set.
  // Guidance visibility must not depend on there being an approval action.
  const summary = typeof recommendation?.summary === 'string' ? recommendation.summary.trim() : '';
  if (!workoutId || !workoutExerciseId || !shouldShowExerciseCoachSuggestions({
    workoutStatus, readOnly, editable, pendingActionCount: pendingActions.length, summary,
  })) return null;

  return (
    <section className="exercise-coach-suggestions" aria-label={`Coach suggestions for ${exerciseName || 'exercise'}`}>
      {summary && <p className="exercise-coach-guidance" ref={element => { if (element) noteCoachOutputCommitted('set_review', String(workoutExerciseId), element, 'suggestion_panel'); }}><span className="exercise-coach-guidance-label">Coach</span>{summary}</p>}
      {pendingActions.length > 0 && <ProposedAction
        actions={pendingActions}
        workoutId={workoutId}
        workoutExerciseId={workoutExerciseId}
        backendService={backendService}
        coachImageUrl={coachImageUrl}
        coachEmoji={coachEmoji}
        onResolved={onResolved}
        onDiscuss={onDiscuss ? () => onDiscuss({
          workoutId: String(workoutId),
          workoutExerciseId: String(workoutExerciseId),
          description: pendingActions.map(action => action.description).filter(Boolean).join('; '),
        }) : undefined}
      />}
    </section>
  );
}

export default ExerciseCoachSuggestions;
